import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCopilot, AskAgentButton } from '../copilot';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  ClipboardText,
  Plus,
  Trophy,
  MapPin,
  Calendar,
  Clock,
  Hand,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  ArrowSquareOut,
} from '@phosphor-icons/react';

export const QuickCanvassPage = () => {
  const { api, user, isSupervisorPlus, isAuditor } = useAuth();
  const { openCopilot, setEntityContext } = useCopilot();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [overtimeHours, setOvertimeHours] = useState('0');

  // New job form
  const [newJob, setNewJob] = useState({
    title: '',
    location: '',
    required_classification: '',
    start_date: '',
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  // Handle URL param for job selection (from Command Center link)
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === jobId);
      if (job) {
        handleSelectJob(job);
      }
    }
  }, [searchParams, jobs]);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/canvass/jobs');
      setJobs(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async (jobId) => {
    try {
      const response = await api.get(`/canvass/bids?job_id=${jobId}`);
      setBids(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bids:', error);
      setBids([]);
    }
  };

  const handleSelectJob = async (job) => {
    // Fetch full job details including source work order
    try {
      const response = await api.get(`/canvass/jobs/${job.id}`);
      setSelectedJob(response.data);
      setEntityContext({ job_id: job.id });
      await fetchBids(job.id);
    } catch (error) {
      // Fallback
      setSelectedJob(job);
      setEntityContext({ job_id: job.id });
      await fetchBids(job.id);
    }
  };

  const navigateToWorkOrder = (woId) => {
    navigate(`/workorders?wo=${woId}`);
  };

  // Ask Agent prompts for job context
  const jobAgentPrompts = selectedJob ? [
    { label: 'Explain Ranking', prompt: 'Explain the ranking system and how bids are ordered for this job.', context: { job_id: selectedJob.id } },
    { label: 'Check Fairness', prompt: 'Check if the bid ranking is fair and follows policy for this job.', context: { job_id: selectedJob.id } },
    { label: 'Draft Override Reason', prompt: 'Help me draft an override reason for awarding this job to a specific bidder.', context: { job_id: selectedJob.id } },
  ] : [];

  const handleSubmitBid = async () => {
    if (!selectedJob) return;

    try {
      const response = await api.post('/canvass/bids', {
        job_id: selectedJob.id,
        overtime_hours_at_bid: parseFloat(overtimeHours) || 0,
      });
      toast.success(`Bid submitted! Your rank: ${response.data.rank}`);
      setBidDialogOpen(false);
      setOvertimeHours('0');
      await fetchBids(selectedJob.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      await api.post('/canvass/jobs', newJob);
      toast.success('Job created successfully');
      setCreateJobOpen(false);
      setNewJob({ title: '', location: '', required_classification: '', start_date: '' });
      await fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const handleOverride = async (userId) => {
    if (!selectedJob) return;

    const reason = prompt('Enter reason for award:');
    if (!reason) return;

    try {
      const response = await api.post(`/canvass/jobs/${selectedJob.id}/award`, {
        user_id: userId,
        reason,
      });
      toast.success('Job awarded successfully');
      
      if (response.data.work_order_synced) {
        toast.info('Work order updated with assignment');
      }
      
      // Refresh job to show award info
      await fetchJobs();
      await handleSelectJob(selectedJob);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to award');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-emerald-100 text-emerald-800">Open</Badge>;
      case 'closed':
        return <Badge className="bg-slate-100 text-slate-800">Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getOutcomeBadge = (outcome) => {
    switch (outcome) {
      case 'awarded':
        return <Badge className="bg-emerald-100 text-emerald-800">Awarded</Badge>;
      case 'not_selected':
        return <Badge className="bg-red-100 text-red-800">Not Selected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
    }
  };

  const userHasBid = bids.some(b => b.user_id === user?.id);

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="canvass-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
            Quick Canvass
          </h1>
          <p className="text-slate-600">
            View open jobs, submit bids, and track your ranking
          </p>
        </div>
        {isSupervisorPlus() && !isAuditor() && (
          <Dialog open={createJobOpen} onOpenChange={setCreateJobOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="create-job-btn">
                <Plus size={20} className="mr-2" />
                Create Job
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Job</DialogTitle>
                <DialogDescription>
                  Create a new job posting for employees to bid on
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateJob} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title</Label>
                  <Input
                    id="title"
                    value={newJob.title}
                    onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                    required
                    data-testid="job-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={newJob.location}
                    onChange={(e) => setNewJob({ ...newJob, location: e.target.value })}
                    data-testid="job-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="classification">Required Classification</Label>
                  <Input
                    id="classification"
                    value={newJob.required_classification}
                    onChange={(e) => setNewJob({ ...newJob, required_classification: e.target.value })}
                    data-testid="job-classification-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={newJob.start_date}
                    onChange={(e) => setNewJob({ ...newJob, start_date: e.target.value })}
                    data-testid="job-date-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-primary" data-testid="submit-job-btn">
                    Create Job
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Jobs List */}
        <div className="lg:col-span-1">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <ClipboardText size={20} />
                Available Jobs
              </CardTitle>
              <CardDescription>
                {jobs.filter(j => j.status === 'open').length} open positions
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                {loading ? (
                  <div className="p-6 text-center text-slate-500">Loading jobs...</div>
                ) : jobs.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">No jobs available</div>
                ) : (
                  jobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => handleSelectJob(job)}
                      className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                        selectedJob?.id === job.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                      }`}
                      data-testid={`job-item-${job.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-slate-900">{job.title}</h3>
                        {getStatusBadge(job.status)}
                      </div>
                      {job.location && (
                        <div className="flex items-center gap-1 text-sm text-slate-500 mb-1">
                          <MapPin size={14} />
                          {job.location}
                        </div>
                      )}
                      {job.start_date && (
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Calendar size={14} />
                          {new Date(job.start_date).toLocaleDateString()}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Job Details & Bids */}
        <div className="lg:col-span-2">
          {selectedJob ? (
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-display text-2xl">{selectedJob.title}</CardTitle>
                    <CardDescription className="mt-1 space-y-1">
                      {selectedJob.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} /> {selectedJob.location}
                        </span>
                      )}
                      {selectedJob.required_classification && (
                        <span className="block">Classification: {selectedJob.required_classification}</span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <AskAgentButton
                      prompts={jobAgentPrompts}
                      onPromptSelect={(prompt, ctx) => openCopilot(prompt, ctx)}
                    />
                    {getStatusBadge(selectedJob.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Source Work Order Link */}
                {selectedJob.source_work_order && (
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <LinkIcon size={18} className="text-primary" />
                        <span className="text-sm font-medium">Source Work Order:</span>
                        <span className="text-sm text-slate-600">{selectedJob.source_work_order.title}</span>
                        <Badge variant="outline" className="text-xs">
                          {selectedJob.source_work_order.status}
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigateToWorkOrder(selectedJob.source_work_order.id)}
                        data-testid="view-source-wo-btn"
                      >
                        <ArrowSquareOut size={14} className="mr-1" />
                        View Work Order
                      </Button>
                    </div>
                  </div>
                )}

                {/* Award Info */}
                {selectedJob.awarded_user_id && (
                  <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <Trophy size={18} className="text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">
                        Job Awarded to: {selectedJob.awarded_user_name || 'Assigned'}
                      </span>
                      {selectedJob.awarded_at && (
                        <span className="text-xs text-emerald-600">
                          on {new Date(selectedJob.awarded_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {selectedJob.source_work_order_id && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Work order has been updated with assignment
                      </p>
                    )}
                  </div>
                )}

                {/* Bid Action */}
                {selectedJob.status === 'open' && !isAuditor() && !isSupervisorPlus() && (
                  <div className="mb-6">
                    {userHasBid ? (
                      <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                        <CheckCircle size={20} />
                        <span>You have already submitted a bid for this job</span>
                      </div>
                    ) : (
                      <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-primary hover:bg-primary/90" data-testid="submit-bid-btn">
                            <Hand size={20} className="mr-2" />
                            Submit Bid
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Submit Your Bid</DialogTitle>
                            <DialogDescription>
                              Enter your current overtime hours. Lower overtime = higher priority.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="overtime">Current Overtime Hours</Label>
                              <Input
                                id="overtime"
                                type="number"
                                min="0"
                                step="0.5"
                                value={overtimeHours}
                                onChange={(e) => setOvertimeHours(e.target.value)}
                                data-testid="overtime-input"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button onClick={handleSubmitBid} className="bg-primary" data-testid="confirm-bid-btn">
                              Confirm Bid
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                )}

                {/* Bids Table */}
                <div>
                  <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-amber-500" />
                    Bid Rankings
                  </h3>
                  {bids.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No bids submitted yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Rank</TableHead>
                          <TableHead>Bidder</TableHead>
                          <TableHead>Overtime Hours</TableHead>
                          <TableHead>Status</TableHead>
                          {isSupervisorPlus() && selectedJob.status === 'open' && (
                            <TableHead className="w-24">Action</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bids.map((bid, index) => (
                          <TableRow key={bid.id} data-testid={`bid-row-${bid.id}`}>
                            <TableCell>
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                                index === 0 ? 'bg-amber-100 text-amber-700' :
                                index === 1 ? 'bg-slate-200 text-slate-700' :
                                index === 2 ? 'bg-amber-50 text-amber-600' :
                                'bg-slate-100 text-slate-600'
                              } font-semibold text-sm`}>
                                {bid.rank_at_bid}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{bid.user_name}</p>
                                <p className="text-sm text-slate-500">{bid.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Clock size={16} className="text-slate-400" />
                                {bid.overtime_hours_at_bid} hrs
                              </div>
                            </TableCell>
                            <TableCell>{getOutcomeBadge(bid.outcome)}</TableCell>
                            {isSupervisorPlus() && selectedJob.status === 'open' && (
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOverride(bid.user_id)}
                                  data-testid={`override-btn-${bid.id}`}
                                >
                                  Award
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <ClipboardText size={48} className="text-slate-300 mb-4" />
                <p className="text-slate-500 text-center">
                  Select a job from the list to view details and bids
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickCanvassPage;
